import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question } from '../../types';
import { getCitoQuestions, getQuestionWithExplanation } from '../../data/questionLoader';
import { SESSION_CONFIG, DIFFICULTY_LABELS } from '../../constants';
import { getLevelTier } from '../../config/levels';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { LatexRenderer } from '../ui/LatexRenderer';
import { AnswerComparison } from '../practice/feedback/AnswerComparison';
import { ExplanationCard } from '../practice/feedback/ExplanationCard';
import { Clock, CheckCircle, ArrowRight, Trophy, Target, BarChart3, ChevronDown, ChevronUp, Brain, BookOpen, Sparkles, Heart, Star, Code } from 'lucide-react';
import { formatSubSkill } from '../../utils/formatters';
import { BreathingExercise } from './BreathingExercise';
interface CitoSimulatorProps {
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, userChoice: number) => void;
}
interface Answer {
  questionId: string;
  userChoice: number;
  isCorrect: boolean;
  timeSpent: number;
  difficulty: number;
  module: string;
  skill?: string;
  subSkill?: string;
  shuffledQuestion?: Question;
}
type SimulatorView = 'intro' | 'test' | 'review' | 'breathing';
// Module icons for debug overlay
const MODULE_ICONS: Record<string, string> = {
  Getalbegrip: '🔢',
  Metriek: '📏',
  Taalbegrip: '📖',
  Spellingregel: '✏️',
  SpellingWW: '📝',
  TijdGeld: '⏰'
};
export function CitoSimulator({
  onExit,
  onAnswer
}: CitoSimulatorProps) {
  const [view, setView] = useState<SimulatorView>('intro');
  const [previousView, setPreviousView] = useState<SimulatorView>('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(SESSION_CONFIG.CITO_TIME_LIMIT_SECONDS);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [understandChecked, setUnderstandChecked] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [isContextExpanded, setIsContextExpanded] = useState(true);
  const [citoQuestions] = useState(() => getCitoQuestions(SESSION_CONFIG.CITO_QUESTION_COUNT));
  const [isPaused, setIsPaused] = useState(false);
  const currentQuestion = citoQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === citoQuestions.length - 1;
  // Keyboard navigation
  useEffect(() => {
    if (view !== 'test' || isPaused) return;
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key >= '1' && e.key <= '4') {
        const index = parseInt(e.key) - 1;
        if (index < currentQuestion.options.length) {
          handleAnswerSelect(index);
        }
      } else if (e.key.toLowerCase() >= 'a' && e.key.toLowerCase() <= 'd') {
        const index = e.key.toLowerCase().charCodeAt(0) - 97;
        if (index < currentQuestion.options.length) {
          handleAnswerSelect(index);
        }
      } else if (e.key === 'Enter' && selectedAnswer !== null) {
        handleNextQuestion();
      } else if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        setShowDebugInfo(!showDebugInfo);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [view, selectedAnswer, currentQuestion, showDebugInfo, isPaused]);
  // Timer
  useEffect(() => {
    if (view !== 'test' || isPaused) return;
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleFinishTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [view, isPaused]);
  // Reset state when question changes
  useEffect(() => {
    if (view === 'test') {
      setQuestionStartTime(Date.now());
      setSelectedAnswer(null);
      setIsContextExpanded(true);
    }
  }, [currentQuestionIndex, view]);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const getTimeStatus = () => {
    const elapsed = SESSION_CONFIG.CITO_TIME_LIMIT_SECONDS - timeRemaining;
    const expectedTime = (currentQuestionIndex + 1) / citoQuestions.length * SESSION_CONFIG.CITO_TIME_LIMIT_SECONDS;
    if (elapsed < expectedTime * 0.8) return 'ahead';
    if (elapsed < expectedTime * 1.2) return 'ontrack';
    return 'behind';
  };
  const handleStartTest = () => {
    setView('test');
    setTimeRemaining(SESSION_CONFIG.CITO_TIME_LIMIT_SECONDS);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setQuestionStartTime(Date.now());
  };
  const handlePause = () => {
    setIsPaused(true);
    setPreviousView(view);
    setView('breathing');
  };
  const handleResume = () => {
    setIsPaused(false);
    setView(previousView === 'intro' ? 'test' : previousView);
    if (previousView === 'intro') {
      handleStartTest();
    }
  };
  const handleAnswerSelect = (choiceIndex: number) => {
    setSelectedAnswer(choiceIndex);
  };
  const handleNextQuestion = () => {
    if (selectedAnswer === null) return;
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    const fullQuestion = getQuestionWithExplanation(currentQuestion.id);
    let shuffledFullQuestion: Question | undefined;
    if (fullQuestion) {
      const shuffledDistractorExplanations: Record<string, string> = {};
      if (fullQuestion.distractorExplanations) {
        currentQuestion.options.forEach((option, newIndex) => {
          const originalIndex = fullQuestion.options.findIndex(opt => opt === option);
          if (originalIndex !== -1 && fullQuestion.distractorExplanations?.[originalIndex.toString()]) {
            shuffledDistractorExplanations[newIndex.toString()] = fullQuestion.distractorExplanations[originalIndex.toString()];
          }
        });
      }
      shuffledFullQuestion = {
        ...fullQuestion,
        options: currentQuestion.options,
        correctAnswer: currentQuestion.correctAnswer,
        distractorExplanations: shuffledDistractorExplanations
      };
    }
    const newAnswer: Answer = {
      questionId: currentQuestion.id,
      userChoice: selectedAnswer,
      isCorrect,
      timeSpent,
      difficulty: currentQuestion.difficulty || 1,
      module: currentQuestion.module,
      skill: currentQuestion.skill,
      subSkill: currentQuestion.subSkill,
      shuffledQuestion: shuffledFullQuestion
    };
    setAnswers([...answers, newAnswer]);
    onAnswer(currentQuestion.id, isCorrect, selectedAnswer);
    if (isLastQuestion) {
      handleFinishTest();
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
    }
  };
  const handleFinishTest = () => {
    setView('review');
  };
  const stats = useMemo(() => {
    if (answers.length === 0) return null;
    const correctCount = answers.filter(a => a.isCorrect).length;
    const incorrectCount = answers.length - correctCount;
    const percentage = Math.round(correctCount / answers.length * 100);
    const totalTimeSpent = answers.reduce((acc, curr) => acc + curr.timeSpent, 0);
    const avgTimePerQuestion = Math.round(totalTimeSpent / answers.length);
    const difficultyStats: Record<string, {
      total: number;
      correct: number;
    }> = {
      easy: {
        total: 0,
        correct: 0
      },
      medium: {
        total: 0,
        correct: 0
      },
      hard: {
        total: 0,
        correct: 0
      }
    };
    answers.forEach(a => {
      const tier = getLevelTier(a.difficulty);
      if (difficultyStats[tier]) {
        difficultyStats[tier].total++;
        if (a.isCorrect) difficultyStats[tier].correct++;
      }
    });
    const skillStats = new Map<string, {
      total: number;
      correct: number;
    }>();
    const subSkillStats = new Map<string, {
      total: number;
      correct: number;
    }>();
    answers.forEach(a => {
      if (a.skill) {
        const skillData = skillStats.get(a.skill) || {
          total: 0,
          correct: 0
        };
        skillData.total++;
        if (a.isCorrect) skillData.correct++;
        skillStats.set(a.skill, skillData);
      }
      if (a.subSkill) {
        const subSkillData = subSkillStats.get(a.subSkill) || {
          total: 0,
          correct: 0
        };
        subSkillData.total++;
        if (a.isCorrect) subSkillData.correct++;
        subSkillStats.set(a.subSkill, subSkillData);
      }
    });
    const strengths: string[] = [];
    const weaknesses: string[] = [];
    subSkillStats.forEach((data, subSkill) => {
      const pct = data.correct / data.total * 100;
      if (data.total >= 2) {
        if (pct >= 80) strengths.push(subSkill);else if (pct < 60) weaknesses.push(subSkill);
      }
    });
    let grade = 'III';
    let gradeColor = 'text-orange-600';
    let gradeText = 'Goed bezig';
    if (percentage >= 90) {
      grade = 'I+';
      gradeColor = 'text-emerald-600';
      gradeText = 'Fantastisch';
    } else if (percentage >= 80) {
      grade = 'I';
      gradeColor = 'text-emerald-500';
      gradeText = 'Super';
    } else if (percentage >= 70) {
      grade = 'II';
      gradeColor = 'text-blue-600';
      gradeText = 'Heel Goed';
    } else if (percentage >= 55) {
      grade = 'III';
      gradeColor = 'text-yellow-600';
      gradeText = 'Goed Bezig';
    } else if (percentage >= 40) {
      grade = 'IV';
      gradeColor = 'text-orange-600';
      gradeText = 'Op Weg';
    } else {
      grade = 'V';
      gradeColor = 'text-rose-600';
      gradeText = 'Nog Even Oefenen';
    }
    return {
      correctCount,
      incorrectCount,
      percentage,
      totalTimeSpent,
      avgTimePerQuestion,
      difficultyStats,
      skillStats,
      subSkillStats,
      strengths,
      weaknesses,
      grade,
      gradeColor,
      gradeText
    };
  }, [answers]);
  // INTRO VIEW
  if (view === 'intro') {
    return <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-violet-50 p-4 md:p-8 flex items-center justify-center" data-id="element-58">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full max-w-2xl" data-id="element-59">
          <Card className="overflow-hidden border-0 shadow-2xl ring-2 ring-indigo-100" data-id="element-60">
            <div className="bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-600 p-8 md:p-10 text-white relative overflow-hidden" data-id="element-61">
              <div className="absolute inset-0 opacity-10" data-id="element-62">
                <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" data-id="element-63" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl" data-id="element-64" />
              </div>

              <div className="relative z-10" data-id="element-65">
                <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-bold mb-4 backdrop-blur-sm border border-white/30" data-id="element-66">
                  <Trophy className="w-4 h-4" data-id="element-67" />
                  <span data-id="element-68">CITO Oefentoets</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold mb-3" data-id="element-69">
                  Laat zien wat je al kunt!
                </h1>
                <p className="text-indigo-100 text-lg leading-relaxed max-w-lg" data-id="element-70">
                  Je gaat {SESSION_CONFIG.CITO_QUESTION_COUNT} vragen maken over
                  rekenen, taal en spelling. Daarna zie je precies wat je al
                  goed kunt.
                </p>
              </div>
            </div>

            <div className="p-6 md:p-8 space-y-6" data-id="element-71">
              <div data-id="element-72">
                <h2 className="text-lg font-bold text-slate-900 mb-4" data-id="element-73">
                  Wat kun je verwachten?
                </h2>
                <div className="grid gap-3" data-id="element-74">
                  <div className="flex items-start gap-3 p-4 bg-indigo-50 rounded-xl border border-indigo-100" data-id="element-75">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-76">
                      <Brain className="w-5 h-5 text-white" data-id="element-77" />
                    </div>
                    <div data-id="element-78">
                      <h3 className="font-semibold text-slate-900 mb-1" data-id="element-79">
                        {SESSION_CONFIG.CITO_QUESTION_COUNT} vragen
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed" data-id="element-80">
                        Over rekenen, taal en spelling. Sommige makkelijk,
                        sommige wat moeilijker.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-violet-50 rounded-xl border border-violet-100" data-id="element-81">
                    <div className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-82">
                      <Clock className="w-5 h-5 text-white" data-id="element-83" />
                    </div>
                    <div data-id="element-84">
                      <h3 className="font-semibold text-slate-900 mb-1" data-id="element-85">
                        Ongeveer 30 minuten
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed" data-id="element-86">
                        Je hoeft niet te haasten. Neem rustig de tijd om na te
                        denken.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100" data-id="element-87">
                    <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-88">
                      <Star className="w-5 h-5 text-white" data-id="element-89" />
                    </div>
                    <div data-id="element-90">
                      <h3 className="font-semibold text-slate-900 mb-1" data-id="element-91">
                        Daarna zie je je resultaten
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed" data-id="element-92">
                        We laten zien wat je al goed kunt en waar je nog kunt
                        oefenen.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5" data-id="element-93">
                <div className="flex gap-3" data-id="element-94">
                  <Heart className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-95" />
                  <div data-id="element-96">
                    <h3 className="font-bold text-amber-900 mb-2" data-id="element-97">
                      Belangrijk om te weten:
                    </h3>
                    <ul className="space-y-2 text-sm text-amber-800" data-id="element-98">
                      <li className="flex items-start gap-2" data-id="element-99">
                        <span className="text-amber-500 mt-1" data-id="element-100">✓</span>
                        <span data-id="element-101">
                          Je kunt niet terug naar een vorige vraag. Dat is niet
                          erg!
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-102">
                        <span className="text-amber-500 mt-1" data-id="element-103">✓</span>
                        <span data-id="element-104">
                          We kijken naar wat je <strong data-id="element-105">al goed</strong> kunt,
                          niet naar fouten.
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-106">
                        <span className="text-amber-500 mt-1" data-id="element-107">✓</span>
                        <span data-id="element-108">
                          Gebruik geen rekenmachine. Kladpapier mag wel!
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-109">
                        <span className="text-amber-500 mt-1" data-id="element-110">✓</span>
                        <span data-id="element-111">
                          Als je iets niet weet, kies dan het antwoord dat het
                          beste lijkt.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border-2 border-slate-200" data-id="element-112">
                <label className="flex items-start gap-3 cursor-pointer group" data-id="element-113">
                  <input type="checkbox" checked={understandChecked} onChange={e => setUnderstandChecked(e.target.checked)} className="w-5 h-5 rounded border-2 border-slate-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 mt-0.5 cursor-pointer" data-id="element-114" />
                  <span className="text-slate-700 font-medium group-hover:text-slate-900" data-id="element-115">
                    Ik snap het en ben klaar om te beginnen
                  </span>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2" data-id="element-116">
                <Button onClick={handleStartTest} disabled={!understandChecked} variant="primary" size="lg" className="flex-1 text-lg h-14 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700" data-id="element-117">
                  <Sparkles className="w-5 h-5 mr-2" data-id="element-118" />
                  Start de toets
                </Button>

                <Button onClick={() => {
                setPreviousView('intro');
                setView('breathing');
              }} variant="ghost" size="lg" className="h-14 text-indigo-600 bg-indigo-50 hover:bg-indigo-100" data-id="element-119">
                  Even rustig worden? 🧘
                </Button>

                <Button onClick={onExit} variant="secondary" size="lg" className="h-14" data-id="element-120">
                  Terug
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>;
  }
  // BREATHING VIEW
  if (view === 'breathing') {
    return <div className="min-h-screen bg-indigo-50 flex items-center justify-center p-4" data-id="element-121">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="w-full max-w-md" data-id="element-122">
          <BreathingExercise onComplete={handleResume} onSkip={handleResume} data-id="element-123" />
        </motion.div>
      </div>;
  }
  // TEST VIEW
  if (view === 'test') {
    const progress = (currentQuestionIndex + 1) / citoQuestions.length * 100;
    const timeStatus = getTimeStatus();
    return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex flex-col" data-id="element-124">
        <header className="bg-white border-b-2 border-indigo-200 sticky top-0 z-10 shadow-md h-12" data-id="element-125">
          <div className="max-w-4xl mx-auto px-4 h-full flex items-center justify-between" data-id="element-126">
            <div className="flex items-center gap-4" data-id="element-127">
              <div className="text-slate-600 text-sm font-medium" data-id="element-128">
                Vraag{' '}
                <span className="text-indigo-600 font-bold text-base" data-id="element-129">
                  {currentQuestionIndex + 1}
                </span>{' '}
                van {citoQuestions.length}
              </div>
            </div>

            <div className="flex items-center gap-3" data-id="element-130">
              {timeStatus === 'ahead' && <span className="text-emerald-600 font-medium text-xs md:text-sm hidden sm:inline" data-id="element-131">
                  Je zit goed op schema
                </span>}
              {timeStatus === 'ontrack' && <span className="text-indigo-600 font-medium text-xs md:text-sm hidden sm:inline" data-id="element-132">
                  Je zit op schema
                </span>}
              {timeStatus === 'behind' && <span className="text-slate-600 font-medium text-xs md:text-sm" data-id="element-133">
                  Nog {formatTime(timeRemaining)}
                </span>}

              <button onClick={handlePause} className="bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full text-xs font-bold hover:bg-indigo-100 transition-colors" data-id="element-134">
                Pauze ⏸️
              </button>

              <button onClick={() => setShowDebugInfo(!showDebugInfo)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" title="Toggle debug info (Ctrl+D)" data-id="element-135">
                <Code className={`w-4 h-4 ${showDebugInfo ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-136" />
              </button>
            </div>
          </div>

          <div className="h-1.5 bg-slate-100 w-full absolute bottom-0 left-0" data-id="element-137">
            <motion.div className="h-full bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-500" initial={{
            width: 0
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} data-id="element-138" />
          </div>
        </header>

        <main className="flex-1 max-w-3xl w-full mx-auto p-3 md:p-6 flex flex-col justify-center" data-id="element-139">
          <AnimatePresence mode="wait" data-id="element-140">
            <motion.div key={currentQuestionIndex} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-141">
              <Card className="p-4 md:p-8 shadow-lg border-0 bg-white ring-2 ring-indigo-100 relative" data-id="element-142">
                {showDebugInfo && <div className="absolute top-4 right-4 bg-slate-900 text-white px-3 py-2 rounded-lg text-xs font-mono space-y-1 shadow-xl z-10" data-id="element-143">
                    <div className="flex items-center gap-2" data-id="element-144">
                      <span className="text-slate-400" data-id="element-145">Module:</span>
                      <span className="font-bold" data-id="element-146">
                        {MODULE_ICONS[currentQuestion.module] || '❓'}{' '}
                        {currentQuestion.module}
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-147">
                      <span className="text-slate-400" data-id="element-148">Niveau:</span>
                      <span className={`font-bold ${getLevelTier(currentQuestion.difficulty) === 'easy' ? 'text-emerald-400' : getLevelTier(currentQuestion.difficulty) === 'medium' ? 'text-amber-400' : 'text-rose-400'}`} data-id="element-149">
                        {DIFFICULTY_LABELS[currentQuestion.difficulty] || 'M6'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-150">
                      <span className="text-slate-400" data-id="element-151">ID:</span>
                      <span className="text-blue-400" data-id="element-152">
                        {currentQuestion.id}
                      </span>
                    </div>
                    {currentQuestion.subSkill && <div className="flex items-center gap-2" data-id="element-153">
                        <span className="text-slate-400" data-id="element-154">Skill:</span>
                        <span className="text-purple-400" data-id="element-155">
                          {currentQuestion.subSkill}
                        </span>
                      </div>}
                  </div>}

                {currentQuestion.context && <div className="mb-4 bg-indigo-50 rounded-xl border border-indigo-100 overflow-hidden" data-id="element-156">
                    <button onClick={() => setIsContextExpanded(!isContextExpanded)} className="w-full flex items-center justify-between p-3 text-left hover:bg-indigo-100/50 transition-colors" data-id="element-157">
                      <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-158">
                        Informatie bij de vraag
                      </span>
                      {isContextExpanded ? <ChevronUp className="w-4 h-4 text-indigo-400" data-id="element-159" /> : <ChevronDown className="w-4 h-4 text-indigo-400" data-id="element-160" />}
                    </button>

                    <AnimatePresence data-id="element-161">
                      {isContextExpanded && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} data-id="element-162">
                          <div className="px-4 pb-4 pt-0 text-slate-700 text-base leading-relaxed border-t border-indigo-100/50 mt-1" data-id="element-163">
                            <LatexRenderer content={currentQuestion.context} data-id="element-164" />
                          </div>
                        </motion.div>}
                    </AnimatePresence>

                    {!isContextExpanded && <div className="px-4 pb-3 text-slate-500 text-sm truncate italic" data-id="element-165">
                        Klik om de informatie te lezen...
                      </div>}
                  </div>}

                <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-slate-900 mb-6 leading-relaxed" data-id="element-166">
                  <LatexRenderer content={currentQuestion.question} data-id="element-167" />
                </h2>

                <div className="space-y-2.5" data-id="element-168">
                  {currentQuestion.options.map((option, index) => <button key={index} onClick={() => handleAnswerSelect(index)} className={`w-full text-left p-3 md:p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-3 md:gap-4 group ${selectedAnswer === index ? 'border-indigo-500 bg-indigo-50 shadow-md scale-[1.01] ring-2 ring-indigo-200' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50 hover:scale-[1.005]'}`} data-id="element-169">
                      <div className={`w-8 h-8 md:w-9 md:h-9 rounded-full border-2 flex items-center justify-center text-sm md:text-base font-bold transition-all flex-shrink-0 ${selectedAnswer === index ? 'border-indigo-500 bg-indigo-600 text-white shadow-lg' : 'border-slate-300 text-slate-400 group-hover:border-indigo-400 group-hover:text-indigo-500'}`} data-id="element-170">
                        {String.fromCharCode(65 + index)}
                      </div>
                      <div className="text-base md:text-lg text-slate-800 leading-relaxed flex-1" data-id="element-171">
                        <LatexRenderer content={option} data-id="element-172" />
                      </div>
                      <div className="hidden md:block text-xs text-slate-400 font-mono opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-173">
                        {index + 1}
                      </div>
                    </button>)}
                </div>

                <div className="hidden md:block mt-4 text-center text-xs text-slate-400" data-id="element-174">
                  Tip: Gebruik toetsen 1-4 of A-D om te kiezen, Enter om door te
                  gaan
                </div>
              </Card>
            </motion.div>
          </AnimatePresence>
        </main>

        <footer className="bg-white border-t-2 border-indigo-200 p-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-20" data-id="element-175">
          <div className="max-w-3xl mx-auto flex justify-end" data-id="element-176">
            <Button onClick={handleNextQuestion} disabled={selectedAnswer === null} variant="primary" size="lg" className="w-full md:w-auto min-w-[180px] h-12 text-lg disabled:opacity-50 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-md" data-id="element-177">
              {isLastQuestion ? 'Klaar!' : 'Volgende vraag'}
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-178" />
            </Button>
          </div>
        </footer>
      </div>;
  }
  // REVIEW VIEW
  if (view === 'review' && stats) {
    return <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-violet-50 p-4 md:p-8" data-id="element-179">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-180">
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center mb-8" data-id="element-181">
            <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-bold mb-4 border border-indigo-200" data-id="element-182">
              <Trophy className="w-4 h-4" data-id="element-183" />
              <span data-id="element-184">CITO Oefentoets voltooid!</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-id="element-185">
              Dit laat zien wat je al kunt
            </h1>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto" data-id="element-186">
              Hieronder zie je precies waar je al goed in bent en waar je nog
              kunt oefenen.
            </p>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.3
          }} className="mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-6 py-3 rounded-full font-bold shadow-lg" data-id="element-187">
              <Sparkles className="w-5 h-5" data-id="element-188" />
              <span data-id="element-189">+100 XP voor voltooien!</span>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.1
        }} data-id="element-190">
            <Card className="p-8 bg-gradient-to-br from-white to-indigo-50 border-0 shadow-xl ring-2 ring-indigo-100" data-id="element-191">
              <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-192">
                <div className="relative w-40 h-40" data-id="element-193">
                  <svg className="w-full h-full transform -rotate-90" data-id="element-194">
                    <circle cx="50%" cy="50%" r="45%" fill="none" stroke="#e0e7ff" strokeWidth="12" data-id="element-195" />
                    <motion.circle cx="50%" cy="50%" r="45%" fill="none" stroke="url(#scoreGradient)" strokeWidth="12" strokeLinecap="round" initial={{
                    pathLength: 0
                  }} animate={{
                    pathLength: stats.percentage / 100
                  }} transition={{
                    duration: 1.5,
                    ease: 'easeOut',
                    delay: 0.3
                  }} style={{
                    strokeDasharray: '283',
                    strokeDashoffset: '0'
                  }} data-id="element-196" />
                    <defs data-id="element-197">
                      <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-198">
                        <stop offset="0%" stopColor="#6366f1" data-id="element-199" />
                        <stop offset="50%" stopColor="#8b5cf6" data-id="element-200" />
                        <stop offset="100%" stopColor="#a855f7" data-id="element-201" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-202">
                    <motion.span className="text-5xl font-bold text-slate-900" initial={{
                    opacity: 0,
                    scale: 0.5
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} transition={{
                    delay: 0.6,
                    type: 'spring'
                  }} data-id="element-203">
                      {stats.correctCount}
                    </motion.span>
                    <span className="text-sm text-slate-500 font-medium" data-id="element-204">
                      van {answers.length}
                    </span>
                  </div>
                </div>

                <div className="flex-1 text-center md:text-left" data-id="element-205">
                  <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-bold mb-3 border border-indigo-200" data-id="element-206">
                    Niveau {stats.grade}
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-2" data-id="element-207">
                    {stats.gradeText}!
                  </h2>
                  <p className="text-slate-600 text-lg mb-4" data-id="element-208">
                    Je hebt{' '}
                    <span className="font-bold text-slate-900" data-id="element-209">
                      {stats.correctCount} vragen
                    </span>{' '}
                    goed beantwoord.
                    {stats.percentage >= 70 && ' Dat is knap!'}
                    {stats.percentage < 70 && stats.percentage >= 55 && ' Goed gedaan!'}
                  </p>
                  <p className="text-sm text-slate-500" data-id="element-210">
                    Dit niveau laat zien hoe ver je bent. Ook moeilijkere vragen
                    goed maken telt extra mee!
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          {(stats.strengths.length > 0 || stats.weaknesses.length > 0) && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-211">
              {stats.strengths.length > 0 && <Card className="p-6 bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200" data-id="element-212">
                  <div className="flex items-center gap-3 mb-4" data-id="element-213">
                    <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg" data-id="element-214">
                      <Star className="w-6 h-6 text-white" data-id="element-215" />
                    </div>
                    <div data-id="element-216">
                      <h3 className="font-bold text-slate-900 text-lg" data-id="element-217">
                        Waar je al goed in bent
                      </h3>
                      <p className="text-sm text-slate-600" data-id="element-218">
                        Dit kun je al super!
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2.5" data-id="element-219">
                    {stats.strengths.slice(0, 5).map(subSkill => <div key={subSkill} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-emerald-100" data-id="element-220">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-221" />
                        <span className="text-slate-700 font-medium" data-id="element-222">
                          {formatSubSkill(subSkill)}
                        </span>
                      </div>)}
                  </div>
                </Card>}

              {stats.weaknesses.length > 0 && <Card className="p-6 bg-gradient-to-br from-indigo-50 to-white border-2 border-indigo-200" data-id="element-223">
                  <div className="flex items-center gap-3 mb-4" data-id="element-224">
                    <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg" data-id="element-225">
                      <Target className="w-6 h-6 text-white" data-id="element-226" />
                    </div>
                    <div data-id="element-227">
                      <h3 className="font-bold text-slate-900 text-lg" data-id="element-228">
                        Hier kun je nog oefenen
                      </h3>
                      <p className="text-sm text-slate-600" data-id="element-229">
                        Dit wordt nog beter!
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2.5" data-id="element-230">
                    {stats.weaknesses.slice(0, 5).map(subSkill => <div key={subSkill} className="flex items-center justify-between gap-3 p-3 bg-white rounded-xl border border-indigo-100" data-id="element-231">
                        <div className="flex items-center gap-3" data-id="element-232">
                          <BookOpen className="w-5 h-5 text-indigo-600 flex-shrink-0" data-id="element-233" />
                          <span className="text-slate-700 font-medium" data-id="element-234">
                            {formatSubSkill(subSkill)}
                          </span>
                        </div>
                        <Button variant="secondary" size="sm" onClick={() => alert('Oefenmodus voor dit onderwerp komt binnenkort!')} className="text-xs h-8 px-3 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-indigo-200" data-id="element-235">
                          Oefen dit
                          <ArrowRight className="w-3 h-3 ml-1.5" data-id="element-236" />
                        </Button>
                      </div>)}
                  </div>
                </Card>}
            </motion.div>}

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} data-id="element-237">
            <Card className="p-6" data-id="element-238">
              <h3 className="font-bold text-slate-900 text-lg mb-4 flex items-center gap-2" data-id="element-239">
                <BarChart3 className="w-5 h-5 text-slate-400" data-id="element-240" />
                Alle vragen bekijken
              </h3>
              <div className="space-y-2" data-id="element-241">
                {answers.map((answer, idx) => <ReviewItem key={idx} answer={answer} index={idx} data-id="element-242" />)}
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="flex justify-center pt-4 pb-8" data-id="element-243">
            <Button onClick={onExit} variant="primary" size="lg" className="min-w-[240px] h-14 text-lg bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700" data-id="element-244">
              Terug naar Dashboard
            </Button>
          </motion.div>
        </div>
      </div>;
  }
  return null;
}
// Review Item component
function ReviewItem({
  answer,
  index
}: {
  answer: Answer;
  index: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const question = answer.shuffledQuestion || getQuestionWithExplanation(answer.questionId);
  if (!question) return null;
  const userAnswerText = question.options[answer.userChoice];
  const correctAnswerText = question.options[question.correctAnswer];
  return <div className={`rounded-xl border-2 transition-all ${answer.isCorrect ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-white'}`} data-id="element-245">
      <div onClick={() => setIsExpanded(!isExpanded)} className="p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-50/50" data-id="element-246">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${answer.isCorrect ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'}`} data-id="element-247">
          {answer.isCorrect ? <CheckCircle className="w-5 h-5" data-id="element-248" /> : <span className="font-bold text-sm" data-id="element-249">{index + 1}</span>}
        </div>

        <div className="flex-1 min-w-0" data-id="element-250">
          <p className="text-slate-700 font-medium truncate" data-id="element-251">
            Vraag {index + 1}: <LatexRenderer content={question.question} data-id="element-252" />
          </p>
          {answer.subSkill && <p className="text-xs text-slate-500 capitalize mt-1" data-id="element-253">
              {formatSubSkill(answer.subSkill)}
            </p>}
        </div>

        <div className="text-slate-400" data-id="element-254">
          {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-255" /> : <ChevronDown className="w-5 h-5" data-id="element-256" />}
        </div>
      </div>

      <AnimatePresence data-id="element-257">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-258">
            <div className="p-4 pt-0 space-y-4" data-id="element-259">
              {question.context && <div className="p-3 bg-white rounded-lg border border-slate-200 text-sm text-slate-600" data-id="element-260">
                  <LatexRenderer content={question.context} data-id="element-261" />
                </div>}

              <div className="text-base font-medium text-slate-900" data-id="element-262">
                <LatexRenderer content={question.question} data-id="element-263" />
              </div>

              <AnswerComparison userAnswer={userAnswerText} correctAnswer={correctAnswerText} isCorrect={answer.isCorrect} mode="compact" data-id="element-264" />

              <ExplanationCard explanation={question.explanation || ''} distractorExplanation={!answer.isCorrect && question.distractorExplanations ? question.distractorExplanations[answer.userChoice.toString()] || 'Helaas, dat was niet het juiste antwoord.' : undefined} isCorrect={answer.isCorrect} mode="compact" data-id="element-265" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}